/**
 * MA-ADMIN-009: Queue Jobs Viewer
 * 
 * Operator tooling for viewing and managing durable queue jobs.
 * Shows verification and dispatch job states with manual retry capability.
 */

import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import {
  RefreshCw,
  Filter,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  PlayCircle,
  RotateCcw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Ban,
} from "lucide-react";
import { toast } from "sonner";

// =============================================================================
// STATUS CONFIGURATION
// =============================================================================

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    icon: Clock,
    description: "Queued, waiting for next run",
  },
  running: {
    label: "Running",
    color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    icon: Loader2,
    description: "Worker currently processing",
  },
  succeeded: {
    label: "Succeeded",
    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    icon: CheckCircle2,
    description: "Completed successfully",
  },
  failed: {
    label: "Failed",
    color: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    icon: AlertTriangle,
    description: "Latest attempt failed, retries may still occur",
  },
  exhausted: {
    label: "Exhausted",
    color: "bg-red-500/20 text-red-400 border-red-500/30",
    icon: XCircle,
    description: "All retries exhausted, needs intervention",
  },
};

const JOB_TYPE_CONFIG = {
  verification: {
    label: "Verification",
    color: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  },
  dispatch: {
    label: "Dispatch",
    color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  },
};

// =============================================================================
// HELPERS
// =============================================================================

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const truncateId = (id, maxLen = 12) => {
  if (!id) return "—";
  if (id.length <= maxLen) return id;
  return `${id.slice(0, maxLen)}...`;
};

// =============================================================================
// QUEUE JOBS VIEWER COMPONENT
// =============================================================================

export default function QueueJobsViewer({ tenantId }) {
  // State
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadIdFilter, setLeadIdFilter] = useState("");
  const [failedOnlyFilter, setFailedOnlyFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Selected job for detail modal
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Retry dialog
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [retryNotes, setRetryNotes] = useState("");
  const [retrying, setRetrying] = useState(false);

  const effectiveTenantId = tenantId || null;

  // =============================================================================
  // FETCH JOBS
  // =============================================================================

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { page, page_size: pageSize };
      
      if (effectiveTenantId) params.tenant_id = effectiveTenantId;
      if (jobTypeFilter && jobTypeFilter !== "all") params.job_type = jobTypeFilter;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (leadIdFilter.trim()) params.lead_id = leadIdFilter.trim();
      if (failedOnlyFilter) params.failed_only = "true";
      
      const response = await api.get("/admin/queue-jobs", { params });
      const data = response.data;
      
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setStats(data.stats || {});
      
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch queue jobs");
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, page, pageSize, jobTypeFilter, statusFilter, leadIdFilter, failedOnlyFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // =============================================================================
  // FETCH JOB DETAIL
  // =============================================================================

  const fetchJobDetail = async (jobId) => {
    setLoadingDetail(true);
    
    try {
      const params = {};
      if (effectiveTenantId) params.tenant_id = effectiveTenantId;
      
      const response = await api.get(`/admin/queue-jobs/${jobId}`, { params });
      setJobDetail(response.data);
      
    } catch (err) {
      toast.error("Failed to fetch job details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    fetchJobDetail(job.id);
  };

  // =============================================================================
  // MANUAL RETRY
  // =============================================================================

  const handleRetry = async () => {
    if (!selectedJob) return;
    
    setRetrying(true);
    
    try {
      const params = {};
      if (effectiveTenantId) params.tenant_id = effectiveTenantId;
      
      const response = await api.post(
        `/admin/queue-jobs/${selectedJob.id}/retry`,
        { notes: retryNotes },
        { params }
      );
      
      const data = response.data;
      
      if (data.success) {
        toast.success(`Retry job created: ${data.new_job_id}`);
        setShowRetryDialog(false);
        setRetryNotes("");
        setSelectedJob(null);
        setJobDetail(null);
        fetchJobs();
      } else {
        toast.error(data.message || "Retry blocked");
      }
      
    } catch (err) {
      toast.error("Failed to retry job");
    } finally {
      setRetrying(false);
    }
  };

  // =============================================================================
  // RENDER STATUS BADGE
  // =============================================================================

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const renderJobTypeBadge = (type) => {
    const config = JOB_TYPE_CONFIG[type] || JOB_TYPE_CONFIG.verification;
    return (
      <Badge className={config.color}>{config.label}</Badge>
    );
  };

  // =============================================================================
  // RENDER HEALTH SUMMARY
  // =============================================================================

  const renderHealthSummary = () => {
    const verStats = stats.verification || {};
    const dispStats = stats.dispatch || {};
    
    const verTotal = (verStats.pending || 0) + (verStats.running || 0) + 
                     (verStats.failed || 0) + (verStats.exhausted || 0);
    const dispTotal = (dispStats.pending || 0) + (dispStats.running || 0) + 
                      (dispStats.failed || 0) + (dispStats.exhausted || 0);
    
    return (
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400">Verification Jobs</div>
                <div className="text-2xl font-bold text-white">{verTotal}</div>
              </div>
              <div className="text-right text-sm">
                {(verStats.exhausted || 0) > 0 && (
                  <div className="text-red-400">{verStats.exhausted} exhausted</div>
                )}
                {(verStats.failed || 0) > 0 && (
                  <div className="text-orange-400">{verStats.failed} retrying</div>
                )}
                {(verStats.pending || 0) > 0 && (
                  <div className="text-blue-400">{verStats.pending} pending</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400">Dispatch Jobs</div>
                <div className="text-2xl font-bold text-white">{dispTotal}</div>
              </div>
              <div className="text-right text-sm">
                {(dispStats.exhausted || 0) > 0 && (
                  <div className="text-red-400">{dispStats.exhausted} exhausted</div>
                )}
                {(dispStats.failed || 0) > 0 && (
                  <div className="text-orange-400">{dispStats.failed} retrying</div>
                )}
                {(dispStats.pending || 0) > 0 && (
                  <div className="text-blue-400">{dispStats.pending} pending</div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="queue-jobs-viewer">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-cyan-400" />
              Queue Jobs
            </CardTitle>
            <CardDescription className="text-slate-400">
              Verification and dispatch retry jobs
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="border-slate-700 text-slate-300"
            >
              <Filter className="w-4 h-4 mr-1" />
              Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchJobs}
              disabled={loading}
              className="border-slate-700 text-slate-300"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Health Summary */}
        {renderHealthSummary()}
        
        {/* Filters - Compact layout */}
        {showFilters && (
          <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-800/50 rounded-lg">
            <div className="space-y-1 w-32">
              <Label className="text-xs text-slate-400">Job Type</Label>
              <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="verification">Verification</SelectItem>
                  <SelectItem value="dispatch">Dispatch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1 w-32">
              <Label className="text-xs text-slate-400">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="exhausted">Exhausted</SelectItem>
                  <SelectItem value="succeeded">Succeeded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1 w-40">
              <Label className="text-xs text-slate-400">Lead ID</Label>
              <Input
                value={leadIdFilter}
                onChange={(e) => setLeadIdFilter(e.target.value)}
                placeholder="Filter by lead..."
                className="h-8 bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs"
              />
            </div>
            
            {/* Quick Filter Toggle - Compact chip style */}
            <Button
              variant={failedOnlyFilter ? "default" : "outline"}
              size="sm"
              onClick={() => setFailedOnlyFilter(!failedOnlyFilter)}
              className={`h-8 ${failedOnlyFilter 
                ? "bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30" 
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
              }`}
            >
              <AlertTriangle className="w-3 h-3 mr-1.5" />
              Failed / Exhausted
            </Button>
          </div>
        )}
        
        {/* Error State - Operator-friendly */}
        {error && (
          <div className="text-center py-6 px-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-400" />
            <p className="text-slate-300 font-medium">Unable to load queue jobs</p>
            <p className="text-sm text-slate-400 mt-1">Please try again or contact support if the issue persists.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchJobs}
              className="mt-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </div>
        )}
        
        {/* Loading State */}
        {loading ? (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
            Loading jobs...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p>No jobs found</p>
            <p className="text-sm">All caught up!</p>
          </div>
        ) : (
          <>
            {/* Jobs Table */}
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Lead ID</TableHead>
                  <TableHead className="text-slate-400">Attempts</TableHead>
                  <TableHead className="text-slate-400">Next Attempt</TableHead>
                  <TableHead className="text-slate-400">Last Error</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} className="border-white/5">
                    <TableCell>{renderJobTypeBadge(job.type)}</TableCell>
                    <TableCell>{renderStatusBadge(job.status)}</TableCell>
                    <TableCell className="text-slate-400 font-mono text-xs">
                      {truncateId(job.lead_id, 12)}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {job.attempt_count} / {job.max_attempts}
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {job.status === "pending" || job.status === "failed"
                        ? formatDateTime(job.next_attempt_at)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs max-w-[200px] truncate">
                      {job.last_error || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewJob(job)}
                        className="text-slate-400 hover:text-slate-200"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-slate-500">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-slate-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  className="border-slate-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
      
      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => { setSelectedJob(null); setJobDetail(null); }}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              Job Details
              {selectedJob && renderStatusBadge(selectedJob.status)}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedJob?.id}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-500" />
            </div>
          ) : jobDetail ? (
            <div className="space-y-4">
              {/* Job Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Type</Label>
                  <div className="mt-1">{renderJobTypeBadge(jobDetail.job.type)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <div className="mt-1">{renderStatusBadge(jobDetail.job.status)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Attempts</Label>
                  <div className="mt-1 text-slate-200">
                    {jobDetail.job.attempt_count} / {jobDetail.job.max_attempts}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500">Created</Label>
                  <div className="mt-1 text-slate-200">{formatDateTime(jobDetail.job.created_at)}</div>
                </div>
              </div>
              
              {/* Lead Info */}
              {jobDetail.lead_info && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <Label className="text-slate-500">Lead</Label>
                  <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
                    <div className="text-slate-400">ID: <span className="text-slate-200 font-mono">{jobDetail.lead_info.id}</span></div>
                    {jobDetail.lead_info.phone && (
                      <div className="text-slate-400">Phone: <span className="text-slate-200">{jobDetail.lead_info.phone}</span></div>
                    )}
                    {jobDetail.lead_info.email && (
                      <div className="text-slate-400">Email: <span className="text-slate-200">{jobDetail.lead_info.email}</span></div>
                    )}
                    <div className="text-slate-400">Delivery: <span className="text-slate-200">{jobDetail.lead_info.outbound_delivery_status || "—"}</span></div>
                  </div>
                </div>
              )}
              
              {/* Destination Info (for dispatch jobs) */}
              {jobDetail.destination_info && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <Label className="text-slate-500">Destination</Label>
                  <div className="mt-1 text-sm text-slate-200">
                    {jobDetail.destination_info.name}
                    {!jobDetail.destination_info.is_active && (
                      <Badge className="ml-2 bg-yellow-500/20 text-yellow-400">Inactive</Badge>
                    )}
                  </div>
                </div>
              )}
              
              {/* Last Error */}
              {jobDetail.job.last_error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <Label className="text-red-400">Last Error</Label>
                  <div className="mt-1 text-sm text-red-300 font-mono">
                    {jobDetail.job.last_error}
                  </div>
                </div>
              )}
              
              {/* Retry Blocked Reason */}
              {!jobDetail.retry_allowed && jobDetail.retry_blocked_reason && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <Label className="text-yellow-400 flex items-center gap-1">
                    <Ban className="w-4 h-4" />
                    Retry Blocked
                  </Label>
                  <div className="mt-1 text-sm text-yellow-300">
                    {jobDetail.retry_blocked_reason}
                  </div>
                </div>
              )}
              
              {/* Audit Logs */}
              {jobDetail.audit_logs && jobDetail.audit_logs.length > 0 && (
                <div>
                  <Label className="text-slate-500">Recent Actions</Label>
                  <div className="mt-1 space-y-1">
                    {jobDetail.audit_logs.map((log) => (
                      <div key={log.id} className="text-xs text-slate-400 flex justify-between">
                        <span>{log.action} by {log.operator_email}</span>
                        <span>{formatDateTime(log.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            {jobDetail?.retry_allowed && (
              <Button
                onClick={() => setShowRetryDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Retry Job
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => { setSelectedJob(null); setJobDetail(null); }}
              className="border-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Retry Confirmation Dialog */}
      <Dialog open={showRetryDialog} onOpenChange={setShowRetryDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Confirm Manual Retry</DialogTitle>
            <DialogDescription className="text-slate-400">
              This will create a new {selectedJob?.type} job for lead {truncateId(selectedJob?.lead_id, 12)}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400">Notes (optional)</Label>
              <Textarea
                value={retryNotes}
                onChange={(e) => setRetryNotes(e.target.value)}
                placeholder="Reason for manual retry..."
                className="mt-1 bg-slate-800 border-slate-700 text-slate-200"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRetryDialog(false)}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRetry}
              disabled={retrying}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {retrying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Retry
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
